import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp, 
  getDocs, 
  getDoc,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';

/** Load Firebase Auth uids from Firestore user profile document ids (requires workspace-scoped user read rules). */
export async function resolveTargetAuthUidsFromUserDocIds(userDocIds: string[]): Promise<string[]> {
  const uids: string[] = [];
  const seen = new Set<string>();
  for (const id of userDocIds) {
    if (!id) continue;
    const snap = await getDoc(doc(db, 'users', id));
    if (snap.exists()) {
      const u = snap.data().uid as string | undefined;
      if (u && !seen.has(u)) {
        seen.add(u);
        uids.push(u);
      }
    }
  }
  return uids;
}
import { 
  NotificationRecord, 
  NotificationStatus,
  CreateNotificationRequest,
  UpdateNotificationRequest,
  MarkNotificationReadRequest,
  NotificationResponse,
  NotificationListResponse,
  NotificationFilter,
  NotificationSort
} from '../types/notifications';
import { User } from '../types/users';
import { ChatThread, ChatMessage } from '../types/chat';

// Create a new notification
export const createNotification = async (
  request: CreateNotificationRequest,
  _currentUser: User
): Promise<NotificationResponse> => {
  try {
    if (!_currentUser.workspace?.id) {
      throw new Error('Current user workspace ID is required');
    }

    if (!request.targetAuthUids || request.targetAuthUids.length === 0) {
      throw new Error('targetAuthUids is required for notifications (Firestore rules)');
    }

    const notificationData: Omit<NotificationRecord, 'id'> = {
      type: request.type,
      title: request.title,
      timestamp: serverTimestamp() as any,
      priority: request.priority,
      status: 'pending' as NotificationStatus,
      targetUsers: request.targetUsers || [],
      targetAuthUids: request.targetAuthUids,
      ...(request.actionUrl && { actionUrl: request.actionUrl }),
      ...(request.sender && { sender: request.sender }),
      readBy: {},
      workspace: _currentUser.workspace,
      createdBy: _currentUser.id
    };

    const docRef = await addDoc(collection(db, 'notifications'), notificationData);
    
    return {
      success: true,
      notification: {
        id: docRef.id,
        ...notificationData
      }
    };
  } catch (error) {
    console.error('Error creating notification:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Get user notifications (query by Firebase Auth uid — matches security rules)
export const getUserNotifications = async (
  authUid: string,
  workspaceId: string,
  filter?: NotificationFilter,
  sort?: NotificationSort,
  limitCount?: number
): Promise<NotificationListResponse> => {
  try {
    if (!authUid || !workspaceId) {
      throw new Error('Auth uid and workspace ID are required');
    }

    let q = query(
      collection(db, 'notifications'),
      where('workspace.id', '==', workspaceId),
      where('targetAuthUids', 'array-contains', authUid)
    );

    // Apply filters
    if (filter?.types && filter.types.length > 0) {
      q = query(q, where('type', 'in', filter.types));
    }

    if (filter?.priorities && filter.priorities.length > 0) {
      q = query(q, where('priority', 'in', filter.priorities));
    }

    if (filter?.statuses && filter.statuses.length > 0) {
      q = query(q, where('status', 'in', filter.statuses));
    }

    if (filter?.dateFrom) {
      q = query(q, where('timestamp', '>=', filter.dateFrom));
    }

    if (filter?.dateTo) {
      q = query(q, where('timestamp', '<=', filter.dateTo));
    }

    // Apply sorting
    const sortField = sort?.field || 'timestamp';
    const sortDirection = sort?.direction || 'desc';
    q = query(q, orderBy(sortField, sortDirection));

    // Apply limit
    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    const snapshot = await getDocs(q);
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as NotificationRecord[];

    return {
      success: true,
      notifications,
      totalCount: notifications.length,
      hasMore: limitCount ? notifications.length >= limitCount : false
    };
  } catch (error) {
    console.error('Error getting user notifications:', error);
    return {
      success: false,
      notifications: [],
      totalCount: 0,
      hasMore: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Mark notification as read
export const markNotificationAsRead = async (
  request: MarkNotificationReadRequest
): Promise<NotificationResponse> => {
  try {
    if (!request.notificationId || !request.userId) {
      throw new Error('Notification ID and user ID are required');
    }

    const notificationRef = doc(db, 'notifications', request.notificationId);
    const readAt = request.readAt || serverTimestamp();

    await updateDoc(notificationRef, {
      [`readBy.${request.userId}`]: readAt
    });

    return {
      success: true
    };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Update notification
export const updateNotification = async (
  request: UpdateNotificationRequest,
  _currentUser: User
): Promise<NotificationResponse> => {
  try {
    if (!request.id) {
      throw new Error('Notification ID is required');
    }

    const notificationRef = doc(db, 'notifications', request.id);
    const updateData: any = {};

    if (request.title !== undefined) updateData.title = request.title;
    if (request.message !== undefined) updateData.message = request.message;
    if (request.priority !== undefined) updateData.priority = request.priority;
    if (request.status !== undefined) updateData.status = request.status;
    if (request.actionUrl !== undefined) updateData.actionUrl = request.actionUrl;
    if (request.actionData !== undefined) updateData.actionData = request.actionData;
    if (request.expiresAt !== undefined) updateData.expiresAt = request.expiresAt;
    if (request.metadata !== undefined) updateData.metadata = request.metadata;

    updateData.updatedAt = serverTimestamp();

    await updateDoc(notificationRef, updateData);

    return {
      success: true
    };
  } catch (error) {
    console.error('Error updating notification:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Check if first message of the day and create notification
export const checkAndCreateDailyMessageNotification = async (
  threadId: string,
  message: ChatMessage,
  thread: ChatThread,
  _currentUser: User
): Promise<boolean> => {
  try {
    if (!_currentUser.workspace?.id) {
      throw new Error('Current user workspace ID is required');
    }

    // Check if this is the first message of the day by comparing with lastMessage timestamp
    const now = new Date();
    // const messageTime = message.timestamp.toDate();
    
    // Get the start of today (00:00:00)
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Check if the last message before this one was from a previous day
    const lastMessageTime = thread.lastMessage.timestamp?.toDate ? 
      thread.lastMessage.timestamp.toDate() : 
      thread.lastMessage.timestamp instanceof Date ? thread.lastMessage.timestamp : new Date();
    const isFirstMessageOfDay = lastMessageTime < startOfToday;

    if (!isFirstMessageOfDay) {
      // Not the first message of the day, no notification needed
      return true;
    }

    // This is the first message of the day, create notification
    const targetUsers = thread.participants.filter(participantId => participantId !== message.fromUserId);
    
    if (targetUsers.length > 0) {
      // Get sender name
      const senderParticipant = thread.participantDetails.find(p => p.userId === message.fromUserId);
      const senderName = senderParticipant?.username || 'Someone';

      // Create notification
      const targetAuthUids = await resolveTargetAuthUidsFromUserDocIds(targetUsers);
      if (targetAuthUids.length === 0) {
        return true;
      }

      const notificationData: Omit<NotificationRecord, 'id'> = {
        type: 'new_message_daily',
        title: `New Message from ${senderName}`,
        timestamp: serverTimestamp() as any,
        priority: 'medium',
        status: 'pending',
        targetUsers,
        targetAuthUids,
        readBy: {},
        workspace: _currentUser.workspace,
        sender: {
          id: message.fromUserId,
          name: senderName
        },
        actionUrl: `/residents/messages?threadId=${threadId}`,
        createdBy: _currentUser.id
      };

      await addDoc(collection(db, 'notifications'), notificationData);
    }

    return true;
  } catch (error) {
    console.error('Error creating daily message notification:', error);
    return false;
  }
};



// Real-time listener for user notifications
export const getUserNotificationsListener = (
  authUid: string,
  workspaceId: string,
  callback: (notifications: NotificationRecord[]) => void,
  filter?: NotificationFilter,
  sort?: NotificationSort,
  limitCount?: number
): (() => void) => {
  try {
    if (!authUid || !workspaceId) {
      throw new Error('Auth uid and workspace ID are required');
    }

    let q = query(
      collection(db, 'notifications'),
      where('workspace.id', '==', workspaceId),
      where('targetAuthUids', 'array-contains', authUid)
    );

    // Apply filters
    if (filter?.types && filter.types.length > 0) {
      q = query(q, where('type', 'in', filter.types));
    }

    if (filter?.priorities && filter.priorities.length > 0) {
      q = query(q, where('priority', 'in', filter.priorities));
    }

    if (filter?.statuses && filter.statuses.length > 0) {
      q = query(q, where('status', 'in', filter.statuses));
    }

    if (filter?.dateFrom) {
      q = query(q, where('timestamp', '>=', filter.dateFrom));
    }

    if (filter?.dateTo) {
      q = query(q, where('timestamp', '<=', filter.dateTo));
    }

    // Apply sorting
    const sortField = sort?.field || 'timestamp';
    const sortDirection = sort?.direction || 'desc';
    q = query(q, orderBy(sortField, sortDirection));

    // Apply limit
    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as NotificationRecord[];
      
      callback(notifications);
    });
  } catch (error) {
    console.error('Error setting up notifications listener:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

// Get notification statistics
export const getNotificationStats = async (
  notificationId: string
): Promise<any> => {
  try {
    const statsRef = doc(db, 'notificationStats', notificationId);
    const statsDoc = await getDoc(statsRef);

    if (statsDoc.exists()) {
      return statsDoc.data();
    }

    return null;
  } catch (error) {
    console.error('Error getting notification stats:', error);
    return null;
  }
};

// Delete notification
export const deleteNotification = async (
  notificationId: string,
  _currentUser: User
): Promise<NotificationResponse> => {
  try {
    if (!notificationId) {
      throw new Error('Notification ID is required');
    }

    // Check if user has permission to delete (created by them or admin)
    const notificationRef = doc(db, 'notifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);

    if (!notificationDoc.exists()) {
      throw new Error('Notification not found');
    }

    const notificationData = notificationDoc.data() as NotificationRecord;
    
    // Only allow deletion if user created it or is admin
    if (notificationData.createdBy !== _currentUser.id) {
      // TODO: Add admin check here
      throw new Error('Permission denied');
    }

    await updateDoc(notificationRef, {
      status: 'failed',
      deletedAt: serverTimestamp(),
      deletedBy: _currentUser.id
    });

    return {
      success: true
    };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};
